"use client"

import { useState } from "react"
import { Handle, Position } from "reactflow"
import "./Node.css"
import "./ResponseNode.css"
import { ArrowLeft, Minimize2, Square, Play, MoreHorizontal, AlertTriangle, Settings } from "lucide-react"
import CopyPasteButtons from "./CopyPasteButtons"

const ResponseNode = ({ data, id }) => {
  const [expanded, setExpanded] = useState(false)
  const [statusCode, setStatusCode] = useState(data.statusCode || 200)
  const [responseBody, setResponseBody] = useState(
    data.responseBody || "{\n  hello: 1234,\n  world: query$.data.something\n}",
  )
  const [activeTab, setActiveTab] = useState("inputs")
  const [showSettings, setShowSettings] = useState(false)
  const [timeout, setTimeout] = useState(10000)
  const [retryCount, setRetryCount] = useState(0)
  const [retryInterval, setRetryInterval] = useState("0ms")
  const [exponential, setExponential] = useState(false)

  const toggleExpand = () => {
    setExpanded(!expanded)
  }

  const toggleSettings = () => {
    setShowSettings(!showSettings)
  }

  return (
    <div className={`response-node-container ${expanded ? "expanded" : ""}`}>
      <div className="response-node">
        <div className="response-node-header">
          <div className="response-node-title">
            <ArrowLeft size={16} className="response-icon" />
            <span>{data.label || "response1"}</span>
          </div>
          <div className="response-node-controls">
            <CopyPasteButtons 
              nodeId={id}
              nodeType="responseNode"
              nodeName={data.label || "response1"}
            />
            <button className="response-control-button" onClick={toggleExpand}>
              <Minimize2 size={14} />
            </button>
            <button className="response-control-button">
              <Square size={14} />
            </button>
            <button className="response-control-button">
              <Play size={14} fill="currentColor" />
            </button>
            <button className="response-control-button">
              <MoreHorizontal size={14} />
            </button>
          </div>
        </div>

        {expanded && (
          <div className="response-node-content">
            <div className="response-field">
              <label>Status code</label>
              <div className="select-wrapper">
                <select value={statusCode} onChange={(e) => setStatusCode(e.target.value)} className="response-select">
                  <option value="200">200</option>
                  <option value="400">400</option>
                  <option value="401">401</option>
                  <option value="500">500</option>
                </select>
              </div>
              <button className="function-button">fx</button>
            </div>

            <div className="response-field">
              <label>Return body</label>
              <textarea
                value={responseBody}
                onChange={(e) => setResponseBody(e.target.value)}
                rows={5}
                className="response-textarea"
              />
            </div>

            <div className="response-tabs">
              <button
                className={`tab-button ${activeTab === "inputs" ? "active" : ""}`}
                onClick={() => setActiveTab("inputs")}
              >
                Inputs
              </button>
              <button
                className={`tab-button ${activeTab === "data" ? "active" : ""}`}
                onClick={() => setActiveTab("data")}
              >
                Data
              </button>
              <button
                className={`tab-button ${activeTab === "json" ? "active" : ""}`}
                onClick={() => setActiveTab("json")}
              >
                JSON
              </button>
              <button
                className={`tab-button ${activeTab === "settings" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("settings")
                  toggleSettings()
                }}
              >
                Settings
              </button>

              <div className="tab-indicators">
                <AlertTriangle size={14} className="warning-icon" />
                <Settings size={14} className="settings-icon" onClick={toggleSettings} />
              </div>
            </div>
          </div>
        )}
      </div>

      {showSettings && (
        <div className="settings-panel">
          <div className="settings-header">
            <button className="back-button">↩</button>
            <button className="close-button" onClick={toggleSettings}>
              ×
            </button>
          </div>

          <div className="settings-content">
            <div className="settings-field">
              <label>Timeout after (ms)</label>
              <input
                type="text"
                value={timeout}
                onChange={(e) => setTimeout(e.target.value)}
                className="settings-input"
              />
            </div>

            <div className="settings-row">
              <div className="settings-field half">
                <label>Retry count</label>
                <input
                  type="text"
                  value={retryCount}
                  onChange={(e) => setRetryCount(e.target.value)}
                  className="settings-input"
                />
              </div>

              <div className="settings-field half">
                <label>Interval (ms)</label>
                <input
                  type="text"
                  value={retryInterval}
                  onChange={(e) => setRetryInterval(e.target.value)}
                  className="settings-input"
                />
              </div>
            </div>

            <div className="settings-checkbox">
              <label>
                <input type="checkbox" checked={exponential} onChange={(e) => setExponential(e.target.checked)} />
                Exponential
              </label>
            </div>

            <div className="settings-footer">
              <div className="settings-label">Finally</div>
              <div className="settings-buttons">
                <button className="settings-button active">Exit</button>
                <button className="settings-button">Continue</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Input handle at the top-left */}
      <div className="input-handle-container">
        <Handle
          type="target"
          position={Position.Left}
          id="b"
          className="connection-handle input-handle"
          style={{
            left: "-15px",
            top: "0",
            transform: "translateY(50%)",
          }}
        />
      </div>
    </div>
  )
}

export default ResponseNode
